import React from "react";
import { useHistory } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";
import PeopleIcon from "@material-ui/icons/People";

import { LocalizedText } from "../LocalizedText";
import { Item, ItemList } from "../ItemList";
import { Panel, PanelHeader, AddButton, DeleteButton } from "../Panel";

import { UserListViewProps } from "./interfaces";
import { dataToItems } from "./transformers";
import { useTranslation } from "../../hooks/i18n";

export const UserListView: React.FC<UserListViewProps> = ({
  data,
  loading,
  error,
  accountId,
  onDelete,
  deleteSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [checkedItems, setCheckedItems] = React.useState<Item[]>([]);

  /**
   * Convert response to <ItemList /> shape
   */
  const dataAsItems = data ? dataToItems(data) : [];

  /**
   * Navigate to specific item when selected
   */
  const handleItemClick = (item: Item) => {
    history.push(`/accounts/summary/${accountId}/users/edit/${item.id}`);
  };

  /**
   * Add or remove an item from state when checked/unchecked
   */
  const handleItemCheck = (item: Item, checked: boolean) => {
    setCheckedItems((checkedItems) => {
      const newCheckedItems = [...checkedItems];
      if (checked) {
        newCheckedItems.push(item);
      } else {
        newCheckedItems.splice(checkedItems.indexOf(item), 1);
      }
      return newCheckedItems;
    });
  };

  /**
   * Handle delete button click
   */
  const handleDelete = async () => {
    if (!window.confirm(t("confirm_delete_items"))) return;
    onDelete(checkedItems);
  };

  /**
   * Navigate to create user when create button pressed
   */
  const handleCreate = () => {
    history.push(`/accounts/summary/${accountId}/users/create`);
  };

  const header = (
    <PanelHeader icon={<PeopleIcon />} title={<LocalizedText text="users" />}>
      {Boolean(checkedItems.length) && <DeleteButton onClick={handleDelete} />}

      <AddButton label="create_user" onClick={handleCreate} />
    </PanelHeader>
  );

  return (
    <Panel header={header}>
      {deleteSuccess && (
        <Alert severity="info">
          <LocalizedText text="delete_success" />
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          <LocalizedText text="an_error_occurred" />
        </Alert>
      )}

      {!error && !loading && !dataAsItems.length && (
        <Alert severity="info">
          <LocalizedText text="no_users" />
        </Alert>
      )}

      <div aria-label={loading ? t("loading") : undefined}>
        <ItemList
          items={dataAsItems}
          loading={loading}
          onItemClick={handleItemClick}
          onItemCheck={handleItemCheck}
          checkboxes
        />
      </div>
    </Panel>
  );
};
